import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { COLORS } from '@src/constants/design-tokens/colors';
import { memo, type ReactElement, useId } from 'react';
import { SkeletonLoader } from './SkeletonLoader';

/**
 * PDFキャンバスコンポーネントのProps型定義
 */
type PdfCanvasProps = {
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  pdfError: { type: string; message: string } | null;
  hideControls?: boolean;
  isRendering: boolean;
};

/**
 * PDFキャンバス表示コンポーネント
 *
 * PDFのキャンバス要素とスクロールコンテナを提供します。
 *
 * @param props - コンポーネントのプロパティ
 * @returns PDFキャンバスコンポーネント
 */
export const PdfCanvas = memo(
  ({
    canvasRef,
    scrollContainerRef,
    pdfError,
    hideControls = false,
    isRendering,
  }: PdfCanvasProps): ReactElement => {
    const scrollInstructionsId = useId();
    const showSkeleton = isRendering && !pdfError;

    return (
      <>
        {/* アクセシビリティ用の隠し説明 */}
        <Typography
          id={scrollInstructionsId}
          variant="body2"
          sx={{
            position: 'absolute',
            left: '-10000px',
            width: '1px',
            height: '1px',
            overflow: 'hidden',
          }}
        >
          矢印キーでスクロール、PageUp/PageDownでページ移動、Home/Endで先頭/末尾に移動できます
        </Typography>

        {/* PDF表示エリア */}
        <Box
          sx={{
            position: 'relative',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            margin: 0,
            height: '100%',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'transparent',
            opacity: pdfError ? 0.3 : 1,
            transition: 'opacity 0.3s ease',
          }}
        >
          {/* PDFエラー時のオーバーレイメッセージ */}
          {pdfError && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: COLORS.focusMode.background.overlay,
                color: COLORS.darkMode.text.primary,
                padding: 3,
                borderRadius: 2,
                textAlign: 'center',
                zIndex: 999,
                maxWidth: '80%',
              }}
            >
              <Typography variant="h6" sx={{ marginBottom: 1 }}>
                PDFファイルの警告
              </Typography>
              <Typography variant="body2">{pdfError.message}</Typography>
            </Box>
          )}

          <Box
            ref={scrollContainerRef}
            sx={{
              flex: 1,
              overflowX: 'hidden',
              overflowY: 'auto',
              position: 'relative',
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-start',
              padding: 0,
              paddingBottom: !hideControls ? '80px' : 0,
              margin: 0,
              boxSizing: 'border-box',
              '&::-webkit-scrollbar': {
                width: '6px',
                height: '0px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: COLORS.scrollbar.thumb,
                borderRadius: '3px',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.4)',
                },
              },
              scrollbarWidth: 'thin',
              scrollbarColor: `${COLORS.scrollbar.thumb} transparent`,
            }}
            aria-busy={showSkeleton}
          >
            {showSkeleton && (
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                  marginBottom: !hideControls ? '80px' : 0,
                }}
              >
                <SkeletonLoader />
              </Box>
            )}
            <canvas
              ref={canvasRef}
              style={{
                display: showSkeleton ? 'none' : 'block',
                backgroundColor: COLORS.background.paper,
                border: `1px solid ${COLORS.neutral[900]}`,
                boxSizing: 'border-box',
                margin: '0 auto',
                padding: 0,
                imageRendering: 'auto',
                maxWidth: '100%',
                width: 'auto',
                height: 'auto',
              }}
            />
          </Box>
        </Box>
      </>
    );
  }
);

PdfCanvas.displayName = 'PdfCanvas';
